import {
  useBulkDeleteUserFoldersMutation,
  useDeleteUserFoldersMutation,
  useGetUserFoldersQuery,
  useUserFolderSearchMutation
} from '@ports/internal-services/folder/folderSlice';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@hooks/Debounce';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { SVGAssets } from '@business/assets/vectors';
import { useDispatch, useSelector } from 'react-redux';
import { setFilterDetails } from '@ports/common/filtersStateSlice';
import { setFolderDetails } from '@ports/common/folderStateSlice';
import {
  getIsFromRecipesPage,
  setIsFromRecipesPage,
  getFolderFilterOptions,
  setFolderFilterOptions
} from '@ports/common/sessionStateSlice';
import { FoldersListProps } from '@business/interfaces/folder';
import { showToast } from '../../Toast/Toast';
import { SelectionBar } from '../../SelectionBar';
import { Filters } from '../../Filters';
import { SearchInput } from '../../HierarchicalMultiSelect/search';
import { FilterSearchEmpty } from '../../FilterSearchEmpty';
import { Table } from '../../Table';
import { ContextMenu } from '../../ContextMenu';
import { Checkbox } from '../../Checkbox';
import { ConfirmationBox } from '../../ConfirmationBox';
import { Button } from '../../Button';
import { CreateFolder } from './CreateFolder';
import { FolderListItem } from './FolderListItem';

const shortLimit = 4; // Short limit for folders list on the main page, can be adjusted as needed

export const FoldersList: FC<FoldersListProps> = ({
  setSelectedPhotos,
  selectedPhotos,
  selectedFolders = [],
  setSelectedFolders = () => {},
  isCreateNewFolder,
  setIsCreateNewFolder,
  isFilterEnabled = false,
  isBackEnabled = false,
  onFolderDeleted
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const moduleName = useSelector(getIsFromRecipesPage);
  const savedFilterOptions = useSelector(getFolderFilterOptions);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [isOpenCreateFolder, setIsOpenCreateFolder] = useState<boolean>(false);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const [isBulkDeleteFoldersConfirm, setIsBulkDeleteFoldersConfirm] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterOptions, setFilterOptions] = useState<any>(
    savedFilterOptions ||
      (isFilterEnabled ? { search: '' } : { limit: 4, offset: 0 })
  );
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('grid');
  const [folders, setFolders] = useState<any>(null);

  const [DeleteFolder] = useDeleteUserFoldersMutation();
  const [BulkDeleteFolders] = useBulkDeleteUserFoldersMutation();
  const [FolderSearch] = useUserFolderSearchMutation();

  const { data: folderData, refetch: folderRefetch } = useGetUserFoldersQuery(
    {
      limit: shortLimit,
      offset: 0,
      ...(moduleName && { module: 'recipe' })
    },
    { skip: isFilterEnabled }
  );

  // Sync local filterOptions to Redux whenever they change
  useEffect(() => {
    dispatch(setFolderFilterOptions(filterOptions));
  }, [filterOptions, dispatch]);

  useEffect(() => {
    if (!isFilterEnabled) {
      folderRefetch();
    }
  }, []);

  useEffect(() => {
    if (!isFilterEnabled) {
      setFolders(folderData?.data);
    }
  }, [folderData?.data]);

  useEffect(() => {
    if (isFilterEnabled) {
      setFilterOptions((prev: any) => {
        if (prev.search === debouncedSearch) {
          return prev; // No update needed if the search term hasn't changed
        }
        return {
          ...prev,
          search: debouncedSearch
        };
      });
    }
  }, [debouncedSearch, isFilterEnabled]);

  useEffect(() => {
    if (isFilterEnabled) {
      folderSearchHandler();
      if (Object.keys(filterOptions).length > 0) {
        dispatch(setFilterDetails(filterOptions));
      }
    }
  }, [filterOptions, isFilterEnabled]);

  const folderSearchHandler = () => {
    // Handle sortBy logic
    let sortBy = 'createdAt';
    let orderBy = 'desc'; // default to most recent

    if (filterOptions?.sortBy) {
      const sortValue = filterOptions.sortBy;
      if (sortValue === 'mostRecent') {
        sortBy = 'createdAt';
        orderBy = 'Desc';
      } else if (sortValue === 'leastRecent') {
        sortBy = 'createdAt';
        orderBy = 'Asc';
      }
    }

    const dataObj = {
      userFolderSearch: {
        search: filterOptions?.search || '',
        sortBy: sortBy,
        orderBy: orderBy,
        favorites: filterOptions?.favorites || false,
        sharedWith: filterOptions?.sharedWith || '',
        people: filterOptions?.people || '',
        things: filterOptions?.things || '',
        locations: filterOptions?.locations || '',
        dates: filterOptions?.date || '',
        interactions: filterOptions?.interactions || '',
        mediaType: filterOptions?.mediaType,
        ...(moduleName && { module: 'recipe' })
      }
    };
    setIsLoading(true);

    FolderSearch(dataObj)
      .then((res: any) => {
        if (res?.data) {
          setFolders(res?.data?.data);
        } else {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t('error', { module: t('folders') })
            }
          });
        }
      })
      .catch(err => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message
          }
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isCreateNewFolder) {
      setIsOpenCreateFolder(true);
      setSelectedFolder(null);
    }
  }, [isCreateNewFolder]);

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolders((prev: string[]) =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
    // Close photo selection when selecting folders
    if (selectedPhotos?.length > 0) {
      setSelectedPhotos?.([]);
    }
  };

  const handleRowClick = (folder: any) => {
    dispatch(
      setFolderDetails({
        folderName: folder?.name,
        folderId: folder?.id,
        isFromRecipesPage: moduleName
      })
    );
    if (moduleName) {
      navigate(`${routePaths.RecipeFolders}/${folder?.id}`);
    } else {
      navigate(`${routePaths.Folders}/${folder?.id}`);
    }
  };

  const handleEditFolder = (folder: any) => {
    setSelectedFolder(folder);
    setIsOpenCreateFolder(true);
  };

  const handleDeleteFolder = (folder: any) => {
    setSelectedFolder(folder);
    setIsDeleteConfirm(true);
  };

  const handleBulkDeleteFolders = () => {
    if (selectedFolders.length === 0) return;
    setIsBulkDeleteFoldersConfirm(true);
  };

  const deleteFolderHandler = (folderId: string) => {
    const dataObj = {
      id: folderId
    };
    DeleteFolder(dataObj)
      .then((res: any) => {
        if (res?.data) {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: t('photosPage.deleteSuccess')
            }
          });
          folderRefetch(); // Add refetch after successful deletion
          onFolderDeleted?.(); // Trigger PhotosList refresh
        } else {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: t('photosPage.deleteError')
            }
          });
        }
      })
      .catch(err => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message
          }
        });
      })
      .finally(() => {
        setSelectedFolder(null);
      });
  };

  const confirmBulkDeleteFolders = async () => {
    try {
      await BulkDeleteFolders({
        body: {
          folderIds: selectedFolders
        }
      }).unwrap();
      showToast({
        type: 'success',
        isAlert: true,
        alertProps: {
          type: 'success',
          message: t('photosPage.bulkDeleteFoldersSuccess')
        }
      });
      setSelectedFolders([]);
      setIsBulkDeleteFoldersConfirm(false);
      folderRefetch();
      onFolderDeleted?.(); // Trigger PhotosList refresh
    } catch (err: any) {
      showToast({
        type: 'error',
        isAlert: true,
        alertProps: {
          type: 'error',
          message: err?.message || t('photosPage.bulkDeleteFoldersError')
        }
      });
      setIsBulkDeleteFoldersConfirm(false);
    }
  };

  const onDeleteConfirmClose = () => {
    setIsDeleteConfirm(false);
    setSelectedFolder(null);
  };

  const contextMenuItems = (folder: any) => [
    {
      id: '1',
      label: t('edit'),
      icon: <SVGAssets.ProfileEditIcon className="w-4 h-4 text-[#505050]" />,
      onClick: () => handleEditFolder(folder)
    },
    {
      id: '2',
      label: t('delete'),
      icon: <SVGAssets.TrashIcon className="w-4 h-4 text-red-600" />,
      onClick: () => handleDeleteFolder(folder)
    }
  ];

  const columns = useMemo(() => {
    return [
      {
        header: t('name'),
        accessorKey: 'name',
        cell: ({ row }: any) => {
          const folder = row.original;
          return (
            <div className="flex items-center space-x-3">
              <Checkbox
                name={`folder-checkbox-${folder?.id}`}
                checked={selectedFolders.includes(folder?.id)}
                onChange={e => {
                  e.stopPropagation();
                  handleFolderSelect(folder?.id);
                }}
                onClick={e => e.stopPropagation()}
              />
              <div className="flex items-center space-x-3">
                <SVGAssets.Folder className="w-4 h-4" />
                <span className="text-sm font-medium text-gray-900">
                  {folder.name}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: t('dateCreated'),
        accessorKey: 'createdAt',
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <span className="text-sm text-gray-500">-</span>;
          const date = new Date(value);
          return (
            <span className="text-sm text-gray-500">
              {date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </span>
          );
        }
      },
      {
        header: t('storage'),
        accessorKey: 'storageSize',
        cell: ({ getValue }: any) => {
          const value = getValue();

          if (!value) return <span className="text-sm text-gray-500">-</span>;

          const sizeInKB = value / 1024;
          const sizeInMB = value / (1024 * 1024);

          return (
            <span className="text-sm text-gray-500">
              {sizeInMB >= 1
                ? `${sizeInMB.toFixed(1)} MB`
                : `${sizeInKB.toFixed(1)} KB`}
            </span>
          );
        }
      },
      {
        header: t('lastViewed'),
        accessorKey: 'folderViewDetail',
        cell: ({ row }: any) => {
          const updatedAt = row?.original?.assetViewDetail?.updatedAt;

          if (!updatedAt)
            return <span className="text-sm text-gray-500">-</span>;

          const date = new Date(updatedAt);

          const day = date.getDate();
          const month = date.toLocaleString('en-US', { month: 'long' });

          // Add st, nd, rd, th
          const suffix =
            day === 1 || day === 21 || day === 31
              ? 'st'
              : day === 2 || day === 22
                ? 'nd'
                : day === 3 || day === 23
                  ? 'rd'
                  : 'th';

          return (
            <span className="text-sm text-gray-500">
              {month} {day}
              {suffix}
            </span>
          );
        }
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: ({ row }: any) => {
          const folder = row.original;
          return (
            <div
              className="flex justify-end relative"
              onClick={e => e.stopPropagation()}>
              <ContextMenu menuItems={contextMenuItems(folder)} />
            </div>
          );
        }
      }
    ];
  }, [selectedFolders]);

  return (
    <div>
      {selectedFolders.length > 0 && (
        <SelectionBar
          selectedCount={selectedFolders.length}
          itemType="folder"
          actions={[
            {
              icon: <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />,
              onClick: handleBulkDeleteFolders,
              title: t('delete')
            }
          ]}
        />
      )}

      {/* Folders Section */}
      {(folders?.count > 0 || isFilterEnabled) && (
        <>
          <div className="mb-5">
            <div className="flex items-center justify-between mb-4">
              {isBackEnabled ? (
                <Button
                  btnStyle="link"
                  className="flex gap-3 items-center"
                  onClick={() => {
                    dispatch(setIsFromRecipesPage(false));
                    navigate(-1);
                  }}>
                  <SVGAssets.PricingArrow className="w-2 mr-2 text-black" />
                  <h2 className="text-lg font-semibold text-gray-800">
                    {t('photosPage.folders')}
                  </h2>
                </Button>
              ) : (
                <h2 className="text-lg font-semibold text-gray-800">
                  {t('photosPage.folders')}
                </h2>
              )}
              {folders?.count > shortLimit && !isFilterEnabled && (
                <Button
                  btnStyle="link"
                  onClick={() => navigate(routePaths.Folders)}
                  className="text-sm font-semibold text-[#49712B] hover:underline">
                  {t('viewAll')}
                </Button>
              )}
            </div>

            {isFilterEnabled && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('photosPage.filterBy')}
                </h3>

                <Filters
                  setFilterOptions={setFilterOptions}
                  selectedView={selectedView}
                  onViewChange={setSelectedView}
                  filterOptions={filterOptions}
                  module="assets"
                />
                <SearchInput
                  className="mt-5"
                  value={filterOptions.search || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFilterOptions({
                      ...filterOptions,
                      search: e.target.value
                    });
                    setSearch(e.target.value);
                  }}
                  onCameraClick={() => {
                    console.log('test');
                  }}
                  isLoading={isLoading}
                />
              </div>
            )}

            {folders?.count > 0 ? (
              <>
                {selectedView === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 3xl:grid-cols-6 gap-4">
                    {folders?.rows?.map((folder: any) => (
                      <FolderListItem
                        key={folder?.id}
                        folderId={folder?.id}
                        folderName={folder?.name}
                        fileCount={
                          moduleName ? folder?.recipeCount : folder?.filesCount
                        }
                        contextMenuItems={contextMenuItems(folder)}
                        isSelected={selectedFolders.includes(folder?.id)}
                        onSelect={handleFolderSelect}
                        isFromRecipesPage={moduleName}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="w-full">
                    <Table
                      tableColumns={columns}
                      tableData={folders?.rows ?? []}
                      className="w-full"
                      onRowClick={handleRowClick}
                    />
                  </div>
                )}
              </>
            ) : (
              isFilterEnabled && <FilterSearchEmpty />
            )}
          </div>
        </>
      )}

      <ConfirmationBox
        isOpen={isDeleteConfirm}
        onCancel={onDeleteConfirmClose}
        onClose={onDeleteConfirmClose}
        onConfirm={() => {
          setIsDeleteConfirm(false);
          deleteFolderHandler(selectedFolder?.id);
        }}
        headerTitle={t('photosPage.deleteFolder')}
        title={t('photosPage.deleteConfirmTitle')}
        icon={<SVGAssets.Delete className="w-20 h-20 pt-3" />}
        confirmText={t('deleteIt')}
        cancelText={t('keepIt')}
      />

      {/* CreateFolder always available */}
      <CreateFolder
        isOpen={isOpenCreateFolder}
        onClose={() => {
          setIsOpenCreateFolder(false);
          setIsCreateNewFolder?.(false);
          setSelectedFolder(null);
        }}
        folderData={selectedFolder}
        folderSearchHandler={() => {
          if (isFilterEnabled) {
            folderRefetch();
          } else {
            folderSearchHandler();
          }
        }}
        {...(moduleName && { module: 'recipe' })}
      />

      {/* Bulk Delete Folders Confirmation - Reuse existing ConfirmationBox */}
      <ConfirmationBox
        isOpen={isBulkDeleteFoldersConfirm}
        onCancel={() => setIsBulkDeleteFoldersConfirm(false)}
        onClose={() => setIsBulkDeleteFoldersConfirm(false)}
        onConfirm={confirmBulkDeleteFolders}
        headerTitle={
          selectedFolders.length > 1
            ? t('photosPage.deleteFolders')
            : t('photosPage.deleteFolder')
        }
        title={
          selectedFolders.length > 1
            ? t('photosPage.deleteMultipleFoldersConfirmTitle', {
                n: selectedFolders.length
              })
            : t('photosPage.deleteConfirmTitle')
        }
        icon={<SVGAssets.Delete className="w-20 h-20 pt-3" />}
        confirmText={t('deleteIt')}
        cancelText={t('keepIt')}
      />
    </div>
  );
};
